// @givmo/passkeys — the transports `@givmo/auth`'s passkey ceremonies ride on, bound to the shared
// api-client (AS-WEB10, AS-WEB11, AS-WEB12; the sign-in's own is `passkey-assertion-transport.ts`).
// `@givmo/auth` owns each ceremony and its error model and stays free of HTTP; these adapters only
// move bytes, through the configured singleton client, so every request inherits the host's
// interceptor stack (Bearer, App Check, `X-Request-Id`, the `{errors:[…]}` → `GivmoError` mapping).
//
// ⚠ A SUCCESS BODY THAT CARRIES A CUSTOM TOKEN IS A BEARER CREDENTIAL. It is returned to the caller
// and nothing else: never stored, never put on an error, never handed to a decode helper something
// else might log. A refusal is built from the ERROR ENVELOPE only (the machine `error_code`, the
// status and the server's render-safe sentence), so nothing from a success body can ride along.

import { firstErrorCode, isGivmoError, sdk } from '@givmo/api-client';
import { PasskeyTransportError, type PasskeyReconfirmationTransport } from '@givmo/auth';

/** Generic copy for a refusal the backend sent no sentence with (e.g. a bare 404 while dark). */
const REFUSED_MESSAGE = 'Could not complete the passkey request.';

/**
 * The structured refusal `@givmo/auth` branches on, from the error envelope alone. `fallbackMessage`
 * words a refusal that carries no envelope (a transport that names its ceremony, as the sign-in does).
 */
export function toPasskeyTransportError(
  error: unknown,
  fallbackMessage: string = REFUSED_MESSAGE,
): PasskeyTransportError {
  if (isGivmoError(error)) {
    return new PasskeyTransportError({
      message: error.message,
      errorCode: firstErrorCode(error),
      status: error.status ?? null,
    });
  }
  return new PasskeyTransportError({ message: fallbackMessage });
}

/**
 * The WebAuthn options out of an options route's body: nested under `options`, or the bare document
 * a future backend might send. `null` for anything else, which the caller turns into a refusal.
 */
export function extractPasskeyOptions(body: unknown): unknown {
  if (typeof body !== 'object' || body === null) return null;
  const wrapped = (body as { options?: unknown }).options;
  if (typeof wrapped === 'object' && wrapped !== null) return wrapped;
  return 'challenge' in body ? body : null;
}

/**
 * The custom token, or `null` for any shape that is not a non-empty string, so a malformed success
 * can never reach `signInWithCustomToken` as `undefined`.
 */
export function extractCustomToken(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const token = (body as { custom_token?: unknown }).custom_token;
  return typeof token === 'string' && token.length > 0 ? token : null;
}

/**
 * The account-bound re-confirmation's two routes (`/users/me/passkeys/reauthentication*`), both in
 * the committed snapshot, so the generated operations carry them. Both send the signed-in account's
 * Bearer token: the binding is the point.
 */
export function createPasskeyReconfirmationTransport(): PasskeyReconfirmationTransport {
  return {
    async requestReconfirmationOptions(init) {
      const { data, error } = await sdk.passkeysStartPasskeyReauthentication({
        signal: init?.signal,
      });
      if (error !== undefined && error !== null) throw toPasskeyTransportError(error);
      const options = extractPasskeyOptions(data);
      if (options === null) {
        throw new PasskeyTransportError({ message: REFUSED_MESSAGE, status: 200 });
      }
      return options;
    },

    async exchangeReconfirmation(assertion, init) {
      const { data, error } = await sdk.passkeysFinishPasskeyReauthentication({
        body: { credential: assertion },
        signal: init?.signal,
      });
      if (error !== undefined && error !== null) throw toPasskeyTransportError(error);
      const customToken = extractCustomToken(data);
      if (customToken === null) {
        throw new PasskeyTransportError({ message: REFUSED_MESSAGE, status: 200 });
      }
      return customToken;
    },
  };
}
